// React imports
import React from 'react';
import { useSelector, useDispatch } from 'react-redux';

// Third-party imports
import moment from 'moment';
import { Moment } from 'moment-timezone';

// Types
import { DiscountType, SavePromocode } from '@spike/promocode-model';
import { RootState } from 'store';

// Actions
import { PromocodesStatus } from '@spike/promocodes-action';

// Hooks
import { usePromoCodes } from '../PromoCodesProvider';

// UI Components
import { ConfirmDialog, OverFullWindow } from 'components/UI';
import Button from 'components/UI/V2/Button/Button';
import { MultiStepForm } from 'components/UI/V3/Forms/MultiStepForm/MultiStepForm';

// Local Components
import { PromoCodeDetailsStep } from './Steps/Detail/PromoCodeDetailsStep';

// Styles
import styles from './AddPromoCode.module.scss';

interface AddPromoCodeProps {
    onClose: () => void;
    onSuccess?: (savedPromoCode: SavePromocode) => void;
    promoCode?: SavePromocode;
    isEditing?: boolean;
}

export const AddPromoCode: React.FC<AddPromoCodeProps> = props => {
    const [showExitPrompt, setShowExitPrompt] = React.useState(false);
    const providerData = usePromoCodes();
    const { savePromoCode, clearError, isConfigurationLocked } = providerData;
    const dispatch = useDispatch();

    const { status } = useSelector((state: RootState) => state.promocodes);

    const [formStep, setFormStep] = React.useState(0);
    const [promoCode, setPromoCode] = React.useState<SavePromocode>(
        props.promoCode || {
            name: '',
            discountAmount: 0,
            discountType: DiscountType.FIXED,
            startAt: moment(),
            endAt: {} as Moment,
            maxCustomerRedeems: 10000,
            maxTotalRedeems: 10000,
            active: true,
            public: true,
            olb: false
        }
    );

    // Status-based state
    const isSaving = status === PromocodesStatus.Saving;
    const hasError = status === PromocodesStatus.Error;
    const isSuccess = status === PromocodesStatus.SaveSuccess;

    // Handle successful saves - dispatch notification and close form
    React.useEffect(() => {
        if (isSuccess) {
            // Close form and notify parent
            if (props.onSuccess) {
                props.onSuccess(promoCode);
            }
        }
    }, [isSuccess, props.onSuccess, promoCode, dispatch]);

    const editStepHandler = (step: number) => {
        setFormStep(step);
    };

    const handleClose = () => {
        setShowExitPrompt(true);
    };

    // Saving in Details handler with status-based flow control
    const promoCodeDetailsHandler = async (formData: Partial<SavePromocode>) => {
        const promo: SavePromocode = {
            ...promoCode,
            ...formData
        };

        // Update local state to preserve user input
        setPromoCode(promo);

        // Clear any existing errors before attempting to save
        if (hasError) {
            clearError();
        }

        // This will set status to Saving, then either SaveSuccess or Error
        await savePromoCode(promo);
    };

    return (
        <OverFullWindow
            showMobileLogo
            title={props.isEditing ? 'Edit Promo Code' : 'Adding Promo Code'}
            onClose={handleClose}
        >
            <ConfirmDialog
                open={showExitPrompt}
                title="Exit Without Saving?"
                question="Your unsaved changes will be lost."
                actions={
                    <>
                        <Button
                            fullWidth
                            size="large"
                            variant="danger"
                            label="Discard"
                            style={{ minWidth: 'unset' }}
                            onClick={props.onClose}
                        />

                        <Button
                            fullWidth
                            size="large"
                            variant="outlined"
                            label="Keep Editing"
                            style={{ margin: 0, minWidth: 'unset' }}
                            onClick={() => setShowExitPrompt(false)}
                        />
                    </>
                }
            />

            <div className={styles.container}>
                <MultiStepForm
                    steps={[
                        {
                            title: 'Promo Code details',
                            component: (
                                <PromoCodeDetailsStep
                                    promoCode={promoCode}
                                    onNext={promoCodeDetailsHandler}
                                    isSaving={isSaving}
                                    lockConfiguration={props.isEditing && isConfigurationLocked}
                                />
                            )
                        }
                    ]}
                    activeStep={formStep}
                    onEdit={editStepHandler}
                />
            </div>
        </OverFullWindow>
    );
};
